import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Lock, ChevronRight, Target, Gamepad2, Home } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DIFF_COLORS = {
  easy: { bg: "bg-[#D6E6FA]", text: "text-[#1B5EA0]", border: "border-[#1B5EA0]", label: "Easy" },
  medium: { bg: "bg-[#FBE7C6]", text: "text-[#8B5E00]", border: "border-[#8B5E00]", label: "Medium" },
  hard: { bg: "bg-[#FFAEBC]", text: "text-[#8B0000]", border: "border-[#8B0000]", label: "Hard" },
};

const DIFF_ORDER = { easy: 0, medium: 1, hard: 2 };

const AssignmentsPage = () => {
  const { enrollmentId } = useParams();
  const [assignments, setAssignments] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentWeek, setCurrentWeek] = useState(0);
  const [weekDifficulty, setWeekDifficulty] = useState({});
  const [weekStats, setWeekStats] = useState({});
  const [expandedWeek, setExpandedWeek] = useState(null);

  // Anonymous session — auto-start silently
  const [sessionId, setSessionId] = useState(null);

  useEffect(() => {
    loadData();
    startAnonSession();
  }, [enrollmentId]);

  const loadData = async () => {
    try {
      const token = localStorage.getItem("access_token");
      const headers = { Authorization: `Bearer ${token}` };

      const [assignmentsRes, attendanceRes] = await Promise.all([
        axios.get(`${API}/assignments/my/${enrollmentId}`, { headers }),
        axios.get(`${API}/attendance/my/${enrollmentId}`, { headers }),
      ]);

      const data = assignmentsRes.data;
      setAssignments(data.assignments || []);
      setCurrentWeek(data.current_week || 0);
      setWeekDifficulty(data.week_difficulty || {});
      setWeekStats(data.week_stats || {});
      setAttendance(attendanceRes.data);

      if (!expandedWeek && data.current_week > 0) {
        setExpandedWeek(data.current_week);
      }
    } catch (error) {
      toast.error("Failed to load assignments");
    } finally {
      setLoading(false);
    }
  };

  const startAnonSession = async () => {
    try {
      const res = await axios.post(`${API}/data/session/start`, {
        age_range: "unknown",
        grade_band: "unknown",
        skill_area: "French"
      });
      setSessionId(res.data.session_id);
    } catch { /* silent */ }
  };

  const endAnonSession = async (completed) => {
    if (!sessionId) return;
    try {
      await axios.post(`${API}/data/session/end`, { session_id: sessionId, completed });
    } catch { /* silent */ }
  };

  // End session when leaving page
  useEffect(() => {
    return () => {
      if (sessionId) {
        navigator.sendBeacon(
          `${API}/data/session/end`,
          JSON.stringify({ session_id: sessionId, completed: false })
        );
      }
    };
  }, [sessionId]);

  const getWeekProgress = (weekNum) => {
    const weekAssignments = assignments.filter((a) => a.week_number === weekNum);
    const completed = weekAssignments.filter((a) => a.completed).length;
    return { completed, total: weekAssignments.length };
  };

  const getDifficultyUnlockStatus = (weekNum, difficulty) => {
    const unlocked = weekDifficulty[String(weekNum)] || "easy";
    return DIFF_ORDER[difficulty] <= DIFF_ORDER[unlocked];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7] py-8 px-4 sm:px-6">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-4 mb-8">
          <Link
            to="/dashboard"
            data-testid="back-to-dashboard"
            className="flex items-center gap-2 text-[#4A4A4A] hover:text-[#0A0A0A]"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Dashboard</span>
          </Link>
          <Link to="/" className="ml-auto flex items-center gap-1 text-sm text-[#3C8DE4] hover:underline font-semibold" data-testid="home-link">
            <Home className="w-4 h-4" /> Home
          </Link>
        </div>

        <h1 className="text-3xl font-bold mb-2" data-testid="assignments-title">Weekly Assignments</h1>
        <p className="text-[#4A4A4A] mb-6">
          Complete assignments after attending each week's class. Questions adapt to your skill level!
        </p>

        {/* Weekly Progress Bar */}
        <div className="grid grid-cols-4 sm:grid-cols-8 gap-2 mb-8" data-testid="weekly-progress">
          {[1, 2, 3, 4, 5, 6, 7, 8].map((week) => {
            const unlocked = week <= currentWeek;
            const progress = getWeekProgress(week);
            const pct = progress.total > 0 ? Math.round((progress.completed / progress.total) * 100) : 0;

            return (
              <button
                key={week}
                data-testid={`week-${week}-progress`}
                onClick={() => unlocked && setExpandedWeek(expandedWeek === week ? null : week)}
                className={`p-2.5 rounded-lg border-2 border-[#0A0A0A] text-center transition-all ${
                  unlocked
                    ? expandedWeek === week
                      ? "bg-[#0A0A0A] text-white"
                      : "bg-[#D6E6FA] hover:bg-[#2D7BD4]"
                    : "bg-[#F0EFEB] cursor-not-allowed"
                }`}
              >
                <div className="text-[10px] opacity-70 mb-0.5">Week</div>
                <div className="font-bold text-lg leading-none mb-1">{week}</div>
                {unlocked ? (
                  <div className="text-[10px] font-medium">{pct}%</div>
                ) : (
                  <Lock className="w-3 h-3 mx-auto opacity-50" />
                )}
              </button>
            );
          })}
        </div>

        {/* Tutor Feedback */}
        {attendance.filter((a) => a.feedback).length > 0 && (
          <div className="brutalist-card p-5 mb-8 bg-[#FBE7C6]" data-testid="tutor-feedback-section">
            <h2 className="font-bold text-base mb-3">Tutor Feedback</h2>
            <div className="space-y-2">
              {attendance
                .filter((a) => a.feedback)
                .map((a, i) => (
                  <div key={i} className="bg-white rounded-lg p-3 border border-[#0A0A0A]">
                    <div className="text-xs text-[#4A4A4A] mb-1">Week {a.week_number}</div>
                    <p className="text-sm">{a.feedback}</p>
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* Assignments List */}
        {currentWeek === 0 ? (
          <div className="brutalist-card p-12 text-center" data-testid="no-assignments-message">
            <Lock className="w-12 h-12 mx-auto mb-4 text-[#4A4A4A]" />
            <h2 className="text-xl font-bold mb-2">No Assignments Unlocked Yet</h2>
            <p className="text-[#4A4A4A]">
              Attend your first class and your tutor will unlock Week 1 assignments.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((weekNum) => {
              const unlocked = weekNum <= currentWeek;
              if (!unlocked) return null;

              const isExpanded = expandedWeek === weekNum;
              const progress = getWeekProgress(weekNum);
              const unlockedDiff = weekDifficulty[String(weekNum)] || "easy";
              const stats = weekStats[String(weekNum)] || {};

              return (
                <div key={weekNum} className="brutalist-card overflow-hidden" data-testid={`week-${weekNum}-section`}>
                  {/* Week Header */}
                  <button
                    onClick={() => setExpandedWeek(isExpanded ? null : weekNum)}
                    data-testid={`week-${weekNum}-toggle`}
                    className="w-full p-4 border-b-2 border-[#0A0A0A] bg-[#D6E6FA] flex items-center justify-between hover:bg-[#2D7BD4] transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <h3 className="font-bold text-base">Week {weekNum}</h3>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${DIFF_COLORS[unlockedDiff].bg} ${DIFF_COLORS[unlockedDiff].text}`}>
                        Level: {DIFF_COLORS[unlockedDiff].label}
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-[#4A4A4A]">
                        {progress.completed}/{progress.total}
                      </span>
                      <ChevronRight className={`w-5 h-5 transition-transform ${isExpanded ? "rotate-90" : ""}`} />
                    </div>
                  </button>

                  {/* Expanded Week Content */}
                  {isExpanded && (
                    <div className="p-4">
                      {/* Play Game Button */}
                      <button
                        onClick={() => window.open(`/game/${enrollmentId}/${weekNum}`, '_blank')}
                        data-testid={`week-${weekNum}-play-btn`}
                        className="w-full mb-4 flex items-center justify-center gap-3 px-6 py-4 bg-[#3C8DE4] text-white font-bold text-lg rounded-xl border-2 border-[#0A0A0A] hover:bg-[#2D7BD4] transition-colors shadow-[3px_3px_0px_0px_rgba(10,10,10,1)]"
                      >
                        <Gamepad2 className="w-6 h-6" />
                        Play Quiz Game
                      </button>

                      {/* Difficulty Accuracy Tracker */}
                      <div className="flex flex-wrap gap-3" data-testid={`week-${weekNum}-stats`}>
                        {["easy", "medium", "hard"].map((diff) => {
                          const diffUnlocked = getDifficultyUnlockStatus(weekNum, diff);
                          const s = stats[diff] || { correct: 0, total: 0, accuracy: 0 };
                          const dc = DIFF_COLORS[diff];

                          return (
                            <div
                              key={diff}
                              className={`flex-1 min-w-[100px] p-3 rounded-lg border-2 ${
                                diffUnlocked ? dc.border : "border-gray-300 opacity-50"
                              }`}
                            >
                              <div className="flex items-center justify-between mb-1.5">
                                <span className={`text-xs font-bold ${diffUnlocked ? dc.text : "text-gray-400"}`}>
                                  {dc.label}
                                </span>
                                {diffUnlocked ? (
                                  s.total > 0 ? (
                                    <span className="text-xs font-semibold">{s.accuracy}%</span>
                                  ) : (
                                    <Target className="w-3 h-3 text-[#4A4A4A]" />
                                  )
                                ) : (
                                  <Lock className="w-3 h-3 text-gray-400" />
                                )}
                              </div>
                              {diffUnlocked && s.total > 0 && (
                                <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                  <div
                                    className={`h-full rounded-full ${dc.bg}`}
                                    style={{ width: `${s.accuracy}%` }}
                                  />
                                </div>
                              )}
                              {diffUnlocked && s.total > 0 && (
                                <div className="text-[10px] text-[#4A4A4A] mt-1">
                                  {s.correct}/{s.total} correct
                                </div>
                              )}
                              {!diffUnlocked && (
                                <div className="text-[10px] text-gray-400 mt-1">Score 70%+ to unlock</div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AssignmentsPage;
